import { useEffect, useState } from 'react';
import { Message, Select, Space, Typography } from '@arco-design/web-react';
import FormModal, { FORM_MODAL_WIDTH } from './FormModal';
import { listRuleSets, runCdrQuality, runChatQuality, type RuleSet } from '../api/quality';

export interface RunQualityModalProps {
  visible: boolean;
  /** call=通话（用 cdrId）/ chat=在线会话（用 sessionId） */
  scene: 'call' | 'chat';
  cdrId?: number | null;
  sessionId?: number | null;
  /** 该记录是否已有报告：有则强制重跑（后端先删旧报告与复核样本，不可撤销） */
  hasReport: boolean;
  onCancel: () => void;
  /** 已成功投递异步质检，外层据此开始轮询报告 */
  onSubmitted: () => void;
}

/**
 * 单条质检规则集选择弹窗（通话详情 / 会话详情共用）：
 * 可多选规则集（空=使用全部启用规则），确认后调用单条质检端点异步投递 worker。
 */
export default function RunQualityModal({
  visible,
  scene,
  cdrId,
  sessionId,
  hasReport,
  onCancel,
  onSubmitted,
}: RunQualityModalProps) {
  const [ruleSets, setRuleSets] = useState<RuleSet[]>([]);
  const [setIDs, setSetIDs] = useState<number[]>([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!visible) return;
    setSetIDs([]);
    listRuleSets()
      .then(({ list }) => setRuleSets(list || []))
      .catch(() => setRuleSets([]));
  }, [visible]);

  const onOk = async () => {
    const id = scene === 'call' ? Number(cdrId || 0) : Number(sessionId || 0);
    if (!id) {
      Message.error(scene === 'call' ? '通话 ID 无效' : '会话 ID 无效');
      return;
    }
    setSubmitting(true);
    try {
      const opts = { rule_set_ids: setIDs.length ? setIDs : undefined, force: hasReport };
      if (scene === 'call') await runCdrQuality(id, opts);
      else await runChatQuality(id, opts);
      Message.success(hasReport ? '已提交重新质检，处理中…' : '已提交质检，处理中…');
      onSubmitted();
    } catch {
      /* 拦截器已提示 */
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <FormModal
      title={hasReport ? '重新质检' : '质检'}
      visible={visible}
      onCancel={onCancel}
      onOk={onOk}
      confirmLoading={submitting}
      okText={hasReport ? '重新质检' : '开始质检'}
      width={FORM_MODAL_WIDTH.compact}
      status={hasReport ? 'warning' : undefined}
    >
      <Space direction="vertical" size={10} style={{ width: '100%' }}>
        {hasReport && (
          <Typography.Text type="warning">
            该记录已有质检报告：重新质检会一并删除现有报告、违规点与复核样本（含人工复核结论），且不可恢复。
          </Typography.Text>
        )}
        <div>
          <Typography.Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>
            规则集（可多选；不选=使用全部启用规则）
          </Typography.Text>
          <Select
            mode="multiple"
            allowClear
            placeholder="全部启用规则"
            value={setIDs}
            onChange={(v) => setSetIDs((v as number[]) || [])}
            options={ruleSets.filter((r) => r.enabled !== false).map((r) => ({ label: r.name, value: r.id }))}
            style={{ width: '100%' }}
          />
        </div>
      </Space>
    </FormModal>
  );
}
